import React, { useEffect, useState, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { getRemotedbUrl, i18nIntl, isNotEmpty, parseClasses } from '../../lib/ep';
import { useClickOutside, useCSS, useKeyEvents } from '../../lib/hooks';

import CountryFlag from './CountryFlag';
import FieldErrorMessage from './FieldErrorMessage';
import FieldLabel from './FieldLabel';
import SearchField from './SearchField';
import Spinner from './Spinner';

const ESCAPE_KEY = 27;

const PrefixRow = ({ country, ...props }) => {
	if(country == undefined) return null;

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const use_prefixes = props?.use_prefixes ?? false;
	const is_closed_view = props?.is_closed_view ?? false;
	const selected = props?.selected ?? false;
	const display_flags = props?.display_flags ?? true;
	const is_enabled = props?.is_enabled ?? true;
	const is_preferred = props?.is_preferred ?? false;
	const is_last = props?.is_last ?? false;

	let main_classes = ['prefix_content'];
	if(is_closed_view) main_classes.push('is_closed_view');
	if(selected) main_classes.push('selected');
	if(!is_enabled) main_classes.push('gui_remove_pointer_events');
	if(is_preferred) {
		main_classes.push('is_preferred');
		if(is_last) main_classes.push('is_last');
	}
	
	return (
		<span
			id={(!is_closed_view) ? `is_${country.k}` : null}
			class={main_classes.join(" ")}
			onClick={(!is_closed_view) ? () => props.onSelect(country) : () => props.onClick()}
		>
			{(display_flags && country.p != undefined && country.p !== false) && (
				<CountryFlag
					country_id={country.k}
					height={false}
				/>
			)}

			{(is_closed_view) ? (
				<span class="prefix mod_align_left">
					{(!use_prefixes || country.p == undefined || country.p === false) ? (
						<span class="country_name no_country">
							<strong class="gui_cutTextOnOverflow gui_text_align_center">{i18n(country.v)}</strong>
						</span>
					) : (
						<span class="country_name">
							<strong class="gui_cutTextOnOverflow">(+{country.p})</strong>
						</span>
					)}
				</span>
			) : (
				<span class="prefix mod_align_left">
					<span class="country_name">
						<strong class="gui_cutTextOnOverflow">{i18n(country.v)}</strong>
					</span>

					{(use_prefixes) && (
						<span>(+{country.p})</span>
					)}
				</span>
			)}
		</span>
	)
};

const CountryPicker = ({ selected, open, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	if(open == undefined) open = false;

	const display_search = props?.display_search ?? true;
	const use_prefixes = props?.use_prefixes ?? false;
	const preferred_countries = props?.preferred_countries ?? [];

	const [ search, setSearch ] = useState('');
	const [ countries, setCountries ] = useState(props?.countries ?? []);

	const countryOffsetTop = (item, container) => {
		const container_rect = container.getBoundingClientRect();

		const rect = item.getBoundingClientRect();
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

		return {
			top:rect.top + scrollTop - container_rect.top - (container_rect.height - rect.height) / 2,
			left:rect.left + scrollLeft - container_rect.left,
		};
	};

	const scrollViewTo = (y, smooth = false) => {
		const country_picker = document.querySelector('.floating_countries_list .c');
		country_picker.scroll({ top:y, behavior:(smooth) ? 'smooth' : 'auto' });
	};

	useEffect(() => {
		if(props.countries.length > 0) setCountries(props.countries);
	}, [ props.countries ]);

	useEffect(() => {
		if(open) {
			const selected_element = document.getElementById(`is_${selected.k}`);
			if(selected_element == undefined) return;

			const country_picker = document.querySelector('.floating_countries_list .c');
			const selected_position = countryOffsetTop(selected_element, country_picker);

			scrollViewTo(selected_position.top);
		} else {
			searchCountry(null, '', false);
		}
	}, [ open ]);

	const searchCountry = (k, v, autoscroll = true) => {
		setSearch(v);

		setCountries((props?.countries ?? []).filter((c) => {
			if(c.k == v) return true;	// country_code equal to v
			if(use_prefixes && c.p == v) return true;	// phone_prefix equal to v
			const reg = new RegExp(v, 'i');
			return reg.test(i18n(c.v));		// country_name containing v
		}));

		if(autoscroll) scrollViewTo(0);
	};

	if(!open) return null;

	const position = props?.position ?? 'bottom';
	const classes = ['floating_countries_list', `in_${position}`];
	
	return (
		<div class={classes.join(" ")}>
			<div class="country_picker">
				{(display_search) && (
					<div class="search_country">
						<SearchField
							_class={{first:true}}
							name="search"
							change={searchCountry}
							placeholder="Type to search"
							value={search}
						/>
					</div>
				)}

				<div class="c">
					{preferred_countries.length > 0 && preferred_countries.map((country, cid) => {
						return (
							<PrefixRow
								key={`pref_country_${cid}`}
								country={country}
								selected={(country.k == selected.k) ? true : false}
								use_prefixes={use_prefixes}
								is_preferred={true}
								is_last={(cid == preferred_countries.length - 1) ? true : false}
								{...props}
							/>
						)
					})}

					{countries.length > 0  && countries.map((country, cid) => {
						return (
							<PrefixRow
								key={`country_${cid}`}
								country={country}
								selected={(country.k == selected.k) ? true : false}
								use_prefixes={use_prefixes}
								{...props}
							/>
						)
					})}
				</div>
			</div>
		</div>
	)
};

export const PhonePrefixField = injectIntl((props) => {
	return (
		<CountryField
			{...props}
			use_prefixes={true}
		/>
	)
});

const CountryField = (props) => {
	useCSS('assets/phone_field.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const avail_countries = props?.avail_countries ?? [];
	const pref_countries = props?.preferred_countries ?? [];
	const use_prefixes = props?.use_prefixes ?? false;
	const has_error = (props.errors && props.errors[props.name]) ? props.errors[props.name] : false;

	const [ country, setCountry ] = useState(props?.country ?? '');
	const [ loading, setLoading ] = useState(true);
	const [ display_countries_list, setDisplayCountriesList ] = useState(false);
	const [ all_countries, setAllCountries ] = useState(false);
	const countries = useRef([]);
	const preferred_countries = useRef([]);
	const menu_ref = useRef(null);

	useClickOutside(menu_ref, () => {
		setDisplayCountriesList(false)
	});

	useKeyEvents({
		[ESCAPE_KEY]:() => {
			setDisplayCountriesList(false);
		},
	});

	useEffect(() => {
		const only_available = props?.only_available ?? true;
		const url = only_available ? '/v24/utils/getAvailableCountriesPhonePrefixes' : '/v24/utils/getAllCountriesPhonePrefixes';
		$.get(getRemotedbUrl(url), (res) => {
			if(res.length > 0) {
				setAllCountries(res);
			}
		}, 'JSON');
	}, []);

	useEffect(() => {
		if(isNotEmpty(props, 'country')) setCountry(props.country);
	}, [ props.country ]);

	useEffect(() => {
		if(all_countries === false) return;
		const filtered_pref_countries = (pref_countries.length > 0) ? pref_countries.map(locale => all_countries.find(obj => obj.k === locale)).filter(Boolean) : [];
		preferred_countries.current = filtered_pref_countries;
		
		const filtered_countries = (avail_countries.length > 0) ? all_countries.filter((c) => (avail_countries.includes(c.k))) : all_countries;
		countries.current = filtered_countries.filter(filtered => 
			!filtered_pref_countries.some(preferred => preferred.k === filtered.k)
		);
		setCountry((props.country != '') ? props.country : '');
		if(props.setPreferredCountries != undefined) props.setPreferredCountries(preferred_countries.current);
		if(props.setCountries != undefined) props.setCountries(countries.current);
		setLoading(false);
	}, [ all_countries, props.avail_countries, props.preferred_countries ]);

	let selected_country = null;
	const in_preferred = preferred_countries.current.find((c) => (c.k == country));
	const in_countries = countries.current.find((c) => (c.k == country));

	if(in_preferred){
		selected_country = in_preferred;
	}else if(in_countries){
		selected_country = in_countries;
	}else if(preferred_countries.current.length>0){
		selected_country = preferred_countries.current[0];
	}else {
		selected_country = (countries.current.length > 1) ? { k:'', v:`-- ${i18n("Select")} --`, p:false } : countries.current[0];
	}

	const change = (k, v) => {
		if(props.change != undefined) props.change(k, v);
	};

	const openCountryPicker = () => {
		if(countries.current.length == 1 && preferred_countries.current.length == 0) return;
		setDisplayCountriesList(true);
	};

	const onSelectCountry = (_country) => {
		setDisplayCountriesList(false);
		if(_country.k == country)return;
		props.onChange('prefix', _country);
		//setCountry(_country.k);
	};

	let classname = props?.classname ?? 'input gui_country_field';
	classname = parseClasses(classname, props._class, 'input');
	if(has_error) classname += ` has_error`;

	const enabled_prefix_row = props?.is_enabled ?? true;

	return (
		<div
			ref={menu_ref}
			class={`react_field type_input ${props?._field_class ?? ''}`}
			onMouseEnter={props?.onMouseEnter ?? null}
			onMouseLeave={props?.onMouseLeave ?? null}
			onFocus={props?.onFocus ?? null}
			onBlur={props?.onBlur ?? null}
			onClick={props?.onClick ?? null}
			style={props?._field_style ?? null}
		>
			<div
				class={classname}
				style={props?._style ?? null}
			>
				{(props.children) && (
					<FieldLabel
						{...props.label_props}
						name={props.name}
						autotranslate={props.autotranslate}
					>{props.children}</FieldLabel>
				)}

				<div class="field_container this_input">
					{(props.custom_input_contents != undefined) && (
						<span class="extra_input">{props.custom_input_contents}</span>
					)}

					<div class="country_selector">
						{(!loading) ? (
							<PrefixRow
								country={selected_country}
								use_prefixes={use_prefixes}
								display_flags={props.display_flags}
								onClick={openCountryPicker}
								is_closed_view={true}
								is_enabled={((countries.current.length + preferred_countries.current.length) > 1 && enabled_prefix_row) ? true : false}
								intl={props.intl}
							/>
						) : (
							<Spinner />
						)}

						<CountryPicker
							preferred_countries={preferred_countries.current}
							countries={countries.current}
							use_prefixes={use_prefixes}
							selected={selected_country}
							open={display_countries_list}
							display_flags={props.display_flags}
							onSelect={onSelectCountry}
							position={props.position}
							display_search={(countries.current.length > 15) ? true : false}
							intl={props.intl}
						/>
					</div>
				</div>

				{(props.after_message != undefined) && (
					<div class="gui_after_message">{props.intl.formatMessage({id:props.after_message})}</div>
				)}

				{(props.after_component != undefined) && (
					<span class="label_additional_msg gui_after_message whole_contents">{props.after_component}</span>
				)}

				{(has_error && (props?.display_error_at_bottom ?? false)) && (
					<FieldErrorMessage
						error_msg_position_class={props.error_msg_position_class}
					>{has_error}</FieldErrorMessage>
				)}
			</div>
		</div>
	)
};

export default injectIntl(CountryField);